import { shadowSizes, useTextEditorState, useUpdatePin } from '@lessonup/pin-renderer';
import {
  FontFamily,
  isDragAndDropItemPinComponent,
  isDragAndDropPin,
  ShadowSettingKey,
  TextPinComponent,
  TypographySettings,
  TypographyType,
} from '@lessonup/pins-shared';
import {
  BodyText,
  IconAlignHorizontalCenter,
  IconAlignHorizontalLeft,
  IconAlignHorizontalRight,
  IconAlignVerticalBottom,
  IconAlignVerticalCenter,
  IconAlignVerticalTop,
  IconFormatListBullet,
  IconFormatListNumbered,
  IconFormatStrikethrough,
  IconFormatSubscript,
  IconFormatSuperscript,
  IconFormatUnderlined,
  SpaceBetween,
  spacing,
  styled,
} from '@lessonup/ui-components';
import { isUndefined } from 'lodash';
import React from 'react';
import {
  ContextSidebarCollapsibleGroup,
  ContextSidebarGroup,
} from '../../../../../../../../components/ContextSidebar/ContextSidebarGroup';
import { useFeatureFlags } from '../../../../../../../../utils/featureFlags/useFeatureFlags';
import {
  useActivePinTheme,
  useEditMode,
  useEffectiveActivePinComponentSettings,
  useNonNullActivePin,
} from '../../../../../../context/EditorContext/hooks/useEditorState';
import { useEditorTranslation } from '../../../../../../hooks/useEditorTranslation';
import {
  createFontFamilyOptions,
  createFontShadowOptions,
  createFontWeightStyleOptions,
  createTypographyOptions,
  FontWeightStyle,
  getFontWeightStyleValue,
  onFontWeightStyleChange,
  setHorizontalTextAlignAction,
  setVerticalTextAlignAction,
  useFontSizePickerHelper,
} from '../../../../../../utils/pinComponentSettings/textPinComponent';
import { teacherTextEditorStore } from '../../../../../../utils/yjs/EditorYjs/teacherTextEditorStore';
import { SettingsToggle } from '../../../../../SettingsToggle/SettingsToggle';
import { NoFocusPicker, NoFocusPickerProps } from '../../../CustomSelect/NoFocusPicker';
import { SteppedNumberInput, SteppedNumberInputProps } from '../../../CustomSelect/SteppedNumberInput';
import { ColorPickerSetting } from '../../../SharedSettings/ColorPickerSetting';
import { BackgroundColorSetting } from '../../sharedComponentSettings/BackgroundColorSetting';
import { BorderColorSetting } from '../../sharedComponentSettings/BorderColorSetting';
import { BorderRadiusSetting } from '../../sharedComponentSettings/BorderRadiusSetting';
import { BorderSizeSetting } from '../../sharedComponentSettings/BorderSizeSetting';
import { LayoutPositionSettingGroup } from '../../sharedComponentSettings/LayoutPositionSetting';
import { LineHeightSetting } from '../../sharedComponentSettings/LineHeightSetting';
import { ParagraphSetting } from '../../sharedComponentSettings/ParagraphSetting';
import { ShadowSetting } from '../../sharedComponentSettings/ShadowSetting';
import { PinComponentSettingsProps } from '../../types';
import { DraggablePinComponentPicker } from '../DropZonePinComponentSettings/DraggablePinComponentPicker';
import { PaddingSetting } from './PaddingSetting';

export const TextPinComponentSettings: React.FC<PinComponentSettingsProps<TextPinComponent>> = ({ pinComponent }) => {
  const { t } = useEditorTranslation();
  const pin = useNonNullActivePin();
  const { isFeatureEnabled } = useFeatureFlags();
  const editMode = useEditMode();
  const { textHandler: textEditorHandler } = useTextEditorState(teacherTextEditorStore);
  const dispatch = useUpdatePin();
  const { typography: typographySettings } = useActivePinTheme();
  const isTextEditMode = editMode.type === 'textEdit';
  const textSettings = useEffectiveActivePinComponentSettings('TEXT', {
    settings: pinComponent.settings,
    textEditorHandler,
    isTextEditMode,
  });

  const shouldUpdateInline = () => textEditorHandler.updateShouldTargetChild(isTextEditMode);

  // const isDragAndDropItem = pinComponent.settings.isDragAndDropItem;

  const fontFamilyPicker: NoFocusPickerProps<FontFamily> = {
    value: textSettings.fontFamily as FontFamily,
    options: createFontFamilyOptions(),
    styleOption: (option) => ({ fontFamily: `"${option?.value}"` }),
    onChange: (value) => {
      if (shouldUpdateInline()) {
        textEditorHandler.fontFamily.set(value);
      } else {
        dispatch({
          type: 'updatePinComponentSettings',
          pinComponentId: pinComponent.id,
          settings: {
            fontFamily: value,
          },
        });
        textEditorHandler.fontFamily.unsetAll();
      }
    },
  };

  const fontShadowValue: ShadowSettingKey = textSettings.fontShadow ?? 'NONE';
  const fontShadowPicker: NoFocusPickerProps<ShadowSettingKey> = {
    value: fontShadowValue,
    valueLabel: t(`contextSidebar.settings.shadows.${fontShadowValue.toLowerCase()}`),
    options: createFontShadowOptions(t),
    styleOption: (option) => ({ textShadow: option?.value && shadowSizes[option.value] }),
    onChange: (value) => {
      dispatch({
        type: 'updatePinComponentSettings',
        pinComponentId: pinComponent.id,
        settings: {
          fontShadow: value,
        },
      });
    },
  };

  const weightValue = getFontWeightStyleValue(textEditorHandler);
  const fontWeightStylePicker: NoFocusPickerProps<FontWeightStyle> = {
    value: weightValue,
    valueLabel: t(`contextSidebar.text.fontWeightStyle.${weightValue}`),
    options: createFontWeightStyleOptions(t),
    styleOption: (option) => ({
      fontFamily: textSettings.fontFamily,
      fontWeight: option?.value === 'bold' || option?.value === 'bold-italic' ? 'bold' : 'normal',
      fontStyle: option?.value === 'regular-italic' || option?.value === 'bold-italic' ? 'italic' : 'normal',
    }),
    onChange: (value) => {
      onFontWeightStyleChange(textEditorHandler, value);
    },
  };

  const typographyKey = textSettings.typographyKey;
  const typographyTypePicker: NoFocusPickerProps<TypographyType> = {
    value: typographyKey,
    valueLabel: typographyKey ? t(`typographyType.${typographyKey}`) : undefined,
    options: createTypographyOptions(),
    styleOption: (option) => styleForTypographyValue(option?.value, option?.value && typographySettings[option?.value]),
    onChange: (value) => {
      dispatch({
        type: 'updatePinComponentSettings',
        pinComponentId: pinComponent.id,
        settings: {
          typographyKey: value,
          fontFamily: undefined,
          fontSize: undefined,
        },
      });
      textEditorHandler.fontFamily.unsetAll();
      textEditorHandler.fontSize.unsetAll();
      if (typographySettings[value]?.fontWeight === '700') {
        textEditorHandler.bold.setAll?.();
      } else {
        textEditorHandler.bold.unsetAll?.();
      }
    },
  };

  const fontSizePicker: SteppedNumberInputProps = useFontSizePickerHelper(
    textEditorHandler,
    pinComponent,
    dispatch,
    isTextEditMode,
    textSettings
  );

  return (
    <>
      <ContextSidebarGroup title={t('contextSidebar.text.font')}>
        <SpaceBetween direction="y" spacing={spacing.size12}>
          {isFeatureEnabled('editorPinTheme') && <NoFocusPicker {...typographyTypePicker} />}
          <NoFocusPicker {...fontFamilyPicker} />
          <SpaceBetween direction="x" spacing={spacing.size12}>
            <NoFocusPicker {...fontWeightStylePicker} />
            <SteppedNumberInput {...fontSizePicker} />
          </SpaceBetween>
          <ColorPickerSetting
            selectedColor={textSettings.fontColor}
            text={t('contextSidebar.settings.textColor')}
            ariaLabel={t('contextSidebar.settings.textColor')}
            hideTransparentOption
            onChange={(color) => {
              if (shouldUpdateInline()) {
                textEditorHandler.color.set(color);
              } else {
                dispatch({
                  type: 'updatePinComponentSettings',
                  pinComponentId: pinComponent.id,
                  settings: {
                    fontColor: color,
                  },
                  debounce: 1000,
                });
                textEditorHandler.color.unsetAll();
              }
            }}
          />
        </SpaceBetween>
      </ContextSidebarGroup>
      {isDragAndDropPin(pin) && isDragAndDropItemPinComponent(pinComponent, pin.settings.linkedPinComponents) && (
        <ContextSidebarCollapsibleGroup
          persistId="dropzoneLinkedComponentSetting"
          title={t('contextSidebar.settings.linkToDropzone')}
          isCollapsed={true}
          openLabel={t('contextSidebar.openLabel')}
          closeLabel={t('contextSidebar.closeLabel')}
        >
          <SpaceBetween direction="y" spacing={spacing.size8}>
            <DraggablePinComponentPicker pinComponent={pinComponent} />
          </SpaceBetween>
        </ContextSidebarCollapsibleGroup>
      )}
      <ContextSidebarCollapsibleGroup
        persistId="textExtraOptionsSettings"
        title={t('contextSidebar.text.styling')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <SpaceBetween direction="x" spacing={spacing.size12} justifyContent="space-between" alignItems="center">
          <BodyText>{t('contextSidebar.text.decoration')}</BodyText>
          <StyledSettingWrapper>
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.underline', {
                enabled: textEditorHandler.underline.get(),
              })}
              icon={<IconFormatUnderlined />}
              checked={textEditorHandler.underline.get()}
              onChange={textEditorHandler.underline.toggle}
            />
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.strikethrough', {
                enabled: textEditorHandler.strikethrough.get(),
              })}
              icon={<IconFormatStrikethrough />}
              checked={textEditorHandler.strikethrough.get()}
              onChange={textEditorHandler.strikethrough.toggle}
            />
          </StyledSettingWrapper>
        </SpaceBetween>
        <SpaceBetween direction="x" spacing={spacing.size12} justifyContent="space-between" alignItems="center">
          <BodyText>{t('contextSidebar.text.listStyle')}</BodyText>
          <StyledSettingWrapper>
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.unorderedList', {
                enabled: textEditorHandler.bulletList.get(),
              })}
              icon={<IconFormatListBullet />}
              checked={textEditorHandler.bulletList.get()}
              onChange={textEditorHandler.bulletList.toggle}
            />
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.orderedList', {
                enabled: textEditorHandler.orderedList.get(),
              })}
              icon={<IconFormatListNumbered />}
              checked={textEditorHandler.orderedList.get()}
              onChange={textEditorHandler.orderedList.toggle}
            />
          </StyledSettingWrapper>
        </SpaceBetween>
        <SpaceBetween direction="x" spacing={spacing.size12} justifyContent="space-between" alignItems="center">
          <BodyText>{t('contextSidebar.text.other')}</BodyText>
          <StyledSettingWrapper>
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.superscript', {
                enabled: textEditorHandler.superscript.get(),
              })}
              icon={<IconFormatSuperscript />}
              checked={textEditorHandler.superscript.get()}
              onChange={textEditorHandler.superscript.toggle}
            />
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.subscript', {
                enabled: textEditorHandler.subscript.get(),
              })}
              icon={<IconFormatSubscript />}
              checked={textEditorHandler.subscript.get()}
              onChange={textEditorHandler.subscript.toggle}
            />
          </StyledSettingWrapper>
        </SpaceBetween>
        <BackgroundColorSetting pinComponent={pinComponent} text={t('contextSidebar.settings.backgroundColor')} />
      </ContextSidebarCollapsibleGroup>
      <LayoutPositionSettingGroup />
      <ContextSidebarCollapsibleGroup
        persistId="textAlignmentSettings"
        title={t('contextSidebar.text.textAlignment.title')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <SpaceBetween direction="x" spacing={spacing.size12} justifyContent="space-between" alignItems="center">
          <StyledSettingWrapper>
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.textAlignment.left', {
                enabled: textSettings.horizontalAlignment === 'left',
              })}
              icon={<IconAlignHorizontalLeft />}
              checked={textSettings.horizontalAlignment === 'left' || isUndefined(textSettings.horizontalAlignment)}
              onChange={() => dispatch(setHorizontalTextAlignAction('left', pinComponent.id))}
            />
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.textAlignment.center', {
                enabled: textSettings.horizontalAlignment === 'center',
              })}
              icon={<IconAlignHorizontalCenter />}
              checked={textSettings.horizontalAlignment === 'center'}
              onChange={() => dispatch(setHorizontalTextAlignAction('center', pinComponent.id))}
            />
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.textAlignment.right', {
                enabled: textSettings.horizontalAlignment === 'right',
              })}
              icon={<IconAlignHorizontalRight />}
              checked={textSettings.horizontalAlignment === 'right'}
              onChange={() => dispatch(setHorizontalTextAlignAction('right', pinComponent.id))}
            />
          </StyledSettingWrapper>
          <StyledSettingWrapper>
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.textAlignment.baseline', {
                enabled: textSettings.verticalAlignment === 'baseline',
              })}
              icon={<IconAlignVerticalTop />}
              checked={textSettings.verticalAlignment === 'baseline' || isUndefined(textSettings.verticalAlignment)}
              onChange={() => dispatch(setVerticalTextAlignAction('baseline', pinComponent.id))}
            />
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.textAlignment.verticalCenter', {
                enabled: textSettings.verticalAlignment === 'center',
              })}
              icon={<IconAlignVerticalCenter />}
              checked={textSettings.verticalAlignment === 'center'}
              onChange={() => dispatch(setVerticalTextAlignAction('center', pinComponent.id))}
            />
            <SettingsToggle
              type="checkbox"
              toggleVariant="icon-only"
              ariaLabel={t('contextSidebar.text.textAlignment.bottom', {
                enabled: textSettings.verticalAlignment === 'end',
              })}
              icon={<IconAlignVerticalBottom />}
              checked={textSettings.verticalAlignment === 'end'}
              onChange={() => dispatch(setVerticalTextAlignAction('end', pinComponent.id))}
            />
          </StyledSettingWrapper>
        </SpaceBetween>
      </ContextSidebarCollapsibleGroup>
      <ContextSidebarCollapsibleGroup
        persistId="textSpacingSettings"
        title={t('contextSidebar.settings.spacing.title')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <LineHeightSetting pinComponent={pinComponent} key={`${pinComponent.id}-text-line-height-setting`} />
        <ParagraphSetting pinComponent={pinComponent} key={`${pinComponent.id}-text-paragraph-setting`} />
      </ContextSidebarCollapsibleGroup>
      <ContextSidebarCollapsibleGroup
        persistId="textTextFittingSettings"
        title={t('contextSidebar.settings.padding.title')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <PaddingSetting pinComponent={pinComponent} key={`${pinComponent.id}-text-fitting-setting`} />
      </ContextSidebarCollapsibleGroup>
      <ContextSidebarCollapsibleGroup
        persistId="textBorderSettings"
        title={t('contextSidebar.settings.border')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <BorderSizeSetting pinComponent={pinComponent} key={`${pinComponent.id}-border-size-setting`} />
        <BorderColorSetting
          pinComponent={pinComponent}
          key={`${pinComponent.id}-border-color-setting`}
          text={t('contextSidebar.settings.borderColor')}
        />
      </ContextSidebarCollapsibleGroup>
      <ContextSidebarCollapsibleGroup
        persistId="textCornerRadiusSettings"
        title={t('contextSidebar.settings.cornerRadius')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <BorderRadiusSetting pinComponent={pinComponent} key={`${pinComponent.id}-border-radius-setting`} />
      </ContextSidebarCollapsibleGroup>
      <ContextSidebarCollapsibleGroup
        persistId="textEffectsSettings"
        title={t('contextSidebar.settings.effects')}
        isCollapsed={true}
        openLabel={t('contextSidebar.openLabel')}
        closeLabel={t('contextSidebar.closeLabel')}
      >
        <SpaceBetween direction="y" spacing={spacing.size8}>
          {isFeatureEnabled('editorTextShadow') && <NoFocusPicker {...fontShadowPicker} />}
          <ShadowSetting pinComponent={pinComponent} key={`${pinComponent.id}-image-shadow-setting`} />
        </SpaceBetween>
      </ContextSidebarCollapsibleGroup>
    </>
  );
};

const StyledSettingWrapper = styled.div`
  display: inline-flex;
`;

export function styleForTypographyValue(
  value: TypographyType | undefined,
  typography?: TypographySettings
): React.CSSProperties | undefined {
  if (!value) {
    return;
  }

  return {
    fontFamily: typography?.fontFamily,
    fontSize: `calc(${typography?.fontSize} / 2.5)`,
    fontWeight: typography?.fontWeight,
  };
}
